import dayjs from 'dayjs';

import { assignLearningToPatients } from './aprender';
import { api, resolvePagination } from './api';
import { assignQuestionnaireToPatients } from './questionnaire';

/**
 * Retrieve patient plans.
 */
export const getPlansTemplates = async (pageLimit, pageToken) => {
    const headers = resolvePagination(pageToken, pageLimit);
    return api.get('/cheart/plans', { headers });
};

/**
 * Retrieve every plan template, following the API pagination tokens.
 */
export const getAllPlanTemplates = async (pageLimit = 50) => {
    const plans = [];
    const visitedTokens = new Set();
    let pageToken = null;

    do {
        const response = await getPlansTemplates(pageLimit, pageToken);
        plans.push(...(Array.isArray(response.data) ? response.data : []));

        const nextToken = response.headers?.['pagination-token'] || null;
        if (!nextToken || visitedTokens.has(nextToken)) break;

        visitedTokens.add(nextToken);
        pageToken = nextToken;
    } while (pageToken);

    return plans;
};

/**
 * Create a new ConnectedHeart plan template.
 * @param {string} name - Plan name.
 * @param {string} description - Plan description.
 * @param {data} data - JSON plan definition.
 */
export const createPlan = async ({ name, description, data }) => {
    const response = await api.post('/cheart/plans', { name, description, data });
    return response.data;
};

/**
 * Delete a ConnectedHeart Plan.
 * @param {string} uuid - The unique identifier of the group to delete.
 */
export const deletePlan = async (uuid) => {
    const response = await api.delete('/cheart/plans', {
        data: { uuid }
    });
    return response.data;
};

/**
 * Fetch a ConnectedHeart Plan.
 * @param {string} uuid - The unique identifier of the plan.
 */
export const fetchPlanByUUID = async (uuid) => {
    const response = await api.post('/cheart/plans/fetch', { uuid });
    return response.data;
};

/**
 * Edit a ConnectedHeart plan template.
 * @param {string} uuid - The unique identifier of the plan to delete.
 * @param {string} name - Plan name.
 * @param {string} description - Plan description.
 * @param {data} data - JSON plan definition.
 */
export const editPlan = async (uuid, name, description, data) => {
    const response = await api.patch('/cheart/plans', {
        uuid,
        name,
        description,
        data
    });
    return response.data;
};

/**
 * Search a ConnectedHeart Plan with Query.
 * @param {string} query - An optional search query to filter plans.
 */
export const searchPlans = async (query = '') => {
    const response = await api.post('/cheart/plans/search', { query });
    return response.data;
};

/**
 * Assign a ConnectedHeart Plan to a Pacient.
 * @param {string} planUUID - The unique identifier of the plan.
 * @param {string} patientUUID - The unique identifier of the patient.
 * @param {object} startDate - The start date of the plan.
 * @param {object} endDate - The end date of the plan.
 * @param {object} options - The options of the plan.
 */
export const assignPlanToPatient = async (
    planUUID,
    patientUUID,
    startDate,
    endDate,
    options = null
) => {
    const payload = {
        plan: planUUID,
        patients: [patientUUID],
        start_date: startDate,
        end_date: endDate,
        ...(options && { options })
    };

    const response = await api.post('/cheart/plan/assign/patients', payload);
    return response.data;
};

const getSundayForWeek = (startDate, weekNumber) => {
    const start = dayjs(startDate);
    const firstSunday = start.day() === 0 ? start : start.add(7 - start.day(), 'day');

    return firstSunday.add(weekNumber - 1, 'week').startOf('day');
};

/**
 * Assign a plan and every questionnaire and learning resource bundled with it.
 */
export const assignCompletePlanToPatient = async ({ plan, patientUUID, startDate }) => {
    const planData = plan?.data || {};
    const durationWeeks = Number(planData.durationWeeks) || 0;
    const normalizedStartDate = dayjs(startDate)
        .startOf('day')
        .format('YYYY-MM-DDTHH:mm');
    const endDate = dayjs(normalizedStartDate)
        .add(durationWeeks, 'week')
        .endOf('day')
        .format('YYYY-MM-DDTHH:mm');

    await assignPlanToPatient(plan.uuid, patientUUID, normalizedStartDate, endDate);

    for (const questionnaire of planData.initialQuestionnaires || []) {
        if (!questionnaire.questionnaireUuid) continue;
        await assignQuestionnaireToPatients(
            questionnaire.questionnaireUuid,
            [patientUUID],
            normalizedStartDate
        );
    }

    for (const questionnaire of planData.questionnaires || []) {
        if (
            !questionnaire.questionnaireUuid ||
            !Array.isArray(questionnaire.assignedWeeks)
        ) {
            continue;
        }

        for (const week of questionnaire.assignedWeeks) {
            if (!Number.isFinite(Number(week)) || Number(week) < 1) continue;
            const assignmentDate = getSundayForWeek(normalizedStartDate, week).format(
                'YYYY-MM-DDTHH:mm'
            );
            await assignQuestionnaireToPatients(
                questionnaire.questionnaireUuid,
                [patientUUID],
                assignmentDate
            );
        }
    }

    for (const content of planData.initialLearningContents || []) {
        if (!content.contentUuid) continue;
        await assignLearningToPatients(
            content.contentUuid,
            [patientUUID],
            normalizedStartDate,
            null
        );
    }

    for (const content of planData.learningContents || []) {
        if (!content.contentUuid || !content.assignedWeek) continue;

        const assignmentDate = getSundayForWeek(
            normalizedStartDate,
            content.assignedWeek
        ).format('YYYY-MM-DDTHH:mm');
        await assignLearningToPatients(
            content.contentUuid,
            [patientUUID],
            assignmentDate,
            null
        );
    }

    return { startDate: normalizedStartDate, endDate };
};

/**
 * Assign a ConnectedHeart Plan to a Group.
 * @param {string} planUUID - The unique identifier of the plan.
 * @param {string} groupUUID - The unique identifier of the group.
 * @param {object} startDate - The start date of the plan.
 * @param {object} endDate - The end date of the plan.
 * @param {object} options - The options of the plan.
 */
export const assignPlanToGroup = async (
    planUUID,
    groupUUID,
    startDate,
    endDate,
    options = null
) => {
    const payload = {
        plan: planUUID,
        group: groupUUID,
        start_date: startDate,
        end_date: endDate,
        ...(options && { options })
    };

    const response = await api.post('/cheart/plan/assign/group', payload);
    return response.data;
};
